import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@gad/shared';
import { apiRequest } from '../lib/api';
import { fetchReferenceLibrary, type BrandReferenceLibrary } from '../lib/brandLibrary';
import { postAdCreatorGenerateFull, waitForAdCreatorGenerationResult } from '../lib/adCreator';

interface Campaign {
  campaign_id: string;
  campaign_name: string;
  recent_cost_micros: number;
  latest_data_date?: string | null;
  window_start_date?: string | null;
  window_end_date?: string | null;
}

interface AdGroup {
  ad_group_id: string;
  ad_group_name: string;
}

interface Keyword {
  keyword_text: string;
  match_type: string;
  status: string;
}

interface AdGroupDetails {
  ad_group_id: string;
  ad_group_name: string;
  campaign_name: string;
  keywords: Keyword[];
  inferred_persona: { id: string; name: string; allScenes: string[] };
}

interface PersonaOption {
  id: string;
  name: string;
  bestScenes: string[];
}

type SpecializationType = 'Persona特式化' | '活動特式化' | '見證代言特式化';

interface OptimizationAd {
  adId: string;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cvr: number;
  specialization: SpecializationType;
  classificationSource: 'tag' | 'rule';
  classificationSignals: string[];
  headlines: string[];
  descriptions: string[];
}

interface SpecializationPerformance {
  specialization: SpecializationType;
  adCount: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cvr: number;
  eligible: boolean;
  belowFixedThresholds: boolean;
  belowMedian: boolean;
  needsOptimization: boolean;
  reason: string;
}

interface OptimizationBrief {
  windowDays: number;
  rules: {
    minImpressions: number;
    ctrThreshold: number;
    cvrThreshold: number;
  };
  adCountEnabled: number;
  ads: OptimizationAd[];
  specializationPerformance: SpecializationPerformance[];
  median: {
    ctr: number;
    cvr: number;
  };
  suggestedSpecialization: SpecializationType | null;
  suggestedReason: string;
}

interface GeneratedHeadlineAsset {
  id: string;
  type: 'H1' | 'H2' | 'H3';
  text: string;
  charCount: number;
}

interface GeneratedDescriptionAsset {
  id: string;
  text: string;
  charCount: number;
  variant: string;
}

interface GeneratedAssets {
  headlines: GeneratedHeadlineAsset[];
  descriptions: GeneratedDescriptionAsset[];
}

type WorkflowStep = 1 | 2 | 3;

const SPECIALIZATION_OPTIONS: SpecializationType[] = ['Persona特式化', '活動特式化', '見證代言特式化'];
function formatPercent(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

function formatCampaignLabel(campaign: Campaign): string {
  return `${campaign.campaign_name} (${formatCurrency(campaign.recent_cost_micros / 1_000_000)} / 14天)`;
}

function toCopyPlainText(assets: GeneratedAssets): string {
  const h = assets.headlines.map((x, i) => `${i + 1}. ${x.text}`).join('\n');
  const d = assets.descriptions.map((x, i) => `${i + 1}. ${x.text}`).join('\n');
  return `Headlines (15)\n${h}\n\nDescriptions (4)\n${d}`;
}

function toCopyTableText(assets: GeneratedAssets): string {
  const headlineTexts = assets.headlines.slice(0, 15).map(h => h.text);
  const descriptionTexts = assets.descriptions.slice(0, 4).map(d => d.text);

  while (headlineTexts.length < 15) headlineTexts.push('');
  while (descriptionTexts.length < 4) descriptionTexts.push('');

  const headers = [
    ...Array.from({ length: 15 }, (_, i) => `Headline ${i + 1}`),
    ...Array.from({ length: 4 }, (_, i) => `Description ${i + 1}`)
  ];

  return `${headers.join('\t')}\n${[...headlineTexts, ...descriptionTexts].join('\t')}`;
}

export function CustomOptimizer() {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [campaignSearch, setCampaignSearch] = useState('');

  const [adGroups, setAdGroups] = useState<AdGroup[]>([]);
  const [selectedAdGroup, setSelectedAdGroup] = useState<AdGroup | null>(null);
  const [adGroupSearch, setAdGroupSearch] = useState('');

  const [adGroupDetails, setAdGroupDetails] = useState<AdGroupDetails | null>(null);
  const [personaOptions, setPersonaOptions] = useState<PersonaOption[]>([]);
  const [persona, setPersona] = useState('');
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [scene, setScene] = useState('');
  const [metaPostUrl, setMetaPostUrl] = useState('');
  const [metaPostText, setMetaPostText] = useState('');
  const [referenceLibrary, setReferenceLibrary] = useState<BrandReferenceLibrary | null>(null);
  const [selectedCelebrityNames, setSelectedCelebrityNames] = useState<string[]>([]);
  const [selectedActivityNames, setSelectedActivityNames] = useState<string[]>([]);

  const [brief, setBrief] = useState<OptimizationBrief | null>(null);
  const [selectedSpecialization, setSelectedSpecialization] = useState<SpecializationType>('Persona特式化');

  const [generatedAssets, setGeneratedAssets] = useState<GeneratedAssets | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [error, setError] = useState('');

  const [step, setStep] = useState<WorkflowStep>(1);

  const filteredCampaigns = campaigns.filter(c =>
    c.campaign_name.toLowerCase().includes(campaignSearch.toLowerCase())
  );
  const filteredAdGroups = adGroups.filter(ag =>
    ag.ad_group_name.toLowerCase().includes(adGroupSearch.toLowerCase())
  );

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    void loadReferenceLibrary();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      loadAdGroups(selectedCampaign.campaign_id);
    } else {
      setAdGroups([]);
      setSelectedAdGroup(null);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    if (selectedAdGroup) {
      void loadAdGroupContext(selectedAdGroup.ad_group_id);
    } else {
      setAdGroupDetails(null);
      setBrief(null);
      setGeneratedAssets(null);
      setKeywords([]);
      setPersona('');
      setStep(1);
    }
  }, [selectedAdGroup]);

  async function loadInitialData() {
    try {
      setLoading(true);
      const [campaignsRes, personasRes] = await Promise.all([
        apiRequest<Campaign[]>('/api/optimizer/campaigns'),
        apiRequest<PersonaOption[]>('/api/optimizer/personas')
      ]);

      if (campaignsRes.success && campaignsRes.data) setCampaigns(campaignsRes.data);
      if (personasRes.success && personasRes.data) setPersonaOptions(personasRes.data);
    } catch (err) {
      console.error(err);
      setError('載入初始資料失敗');
    } finally {
      setLoading(false);
    }
  }

  async function loadReferenceLibrary() {
    try {
      setReferenceLibrary(await fetchReferenceLibrary());
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入品牌資料庫失敗');
    }
  }

  function toggleNamedValue(target: string, values: string[], setter: (values: string[]) => void) {
    if (values.includes(target)) {
      setter(values.filter((value) => value !== target));
      return;
    }
    setter([...values, target]);
  }

  async function loadAdGroups(campaignId: string) {
    const res = await apiRequest<AdGroup[]>(`/api/optimizer/campaigns/${campaignId}/ad-groups`);
    if (res.success && res.data) {
      setAdGroups(res.data);
    }
  }

  async function loadAdGroupContext(adGroupId: string) {
    try {
      setLoadingBrief(true);
      setError('');
      setGeneratedAssets(null);
      setMetaPostUrl('');
      setMetaPostText('');
      setStep(1);

      const [detailsRes, briefRes] = await Promise.all([
        apiRequest<AdGroupDetails>(`/api/optimizer/ad-groups/${adGroupId}/details`),
        apiRequest<OptimizationBrief>(`/api/optimizer/ad-groups/${adGroupId}/optimization-brief`)
      ]);

      if (detailsRes.success && detailsRes.data) {
        setAdGroupDetails(detailsRes.data);
        setPersona(detailsRes.data.inferred_persona.name);
        setKeywords(detailsRes.data.keywords);
      }

      if (briefRes.success && briefRes.data) {
        setBrief(briefRes.data);
        setSelectedSpecialization(briefRes.data.suggestedSpecialization || 'Persona特式化');
      }
    } catch (err) {
      console.error(err);
      setError('載入廣告組合資料失敗');
    } finally {
      setLoadingBrief(false);
    }
  }

  async function generateAssets() {
    if (!selectedAdGroup) return;
    if (keywords.length === 0) {
      setError('此廣告組合沒有可用關鍵字，無法生成文案');
      return;
    }

    if (selectedSpecialization === '見證代言特式化' && (referenceLibrary?.celebrities.active.length || 0) === 0) {
      setError('品牌資料庫目前沒有可用名人素材，請先到品牌資料庫建立');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      setCopyMessage('');
      setGeneratedAssets(null);

      const start = await postAdCreatorGenerateFull<GeneratedAssets>({
        adGroupId: selectedAdGroup.ad_group_id,
        keywords: keywords.map(k => k.keyword_text),
        persona,
        specialization: selectedSpecialization,
        scene: scene || undefined,
        celebrityNames: selectedCelebrityNames,
        activityNames: selectedActivityNames,
        metaPostUrl,
        metaPostText
      });

      if (!start.success || !start.generationId) {
        setError(start.error || '生成任務建立失敗');
        return;
      }

      const result = await waitForAdCreatorGenerationResult<GeneratedAssets>(start.generationId);
      if (!result.success || result.status !== 'completed' || !result.generated) {
        setError(result.error || '生成失敗');
        return;
      }

      setGeneratedAssets(result.generated);
      setStep(3);
    } catch (err) {
      console.error(err);
      setError('生成文案時發生錯誤');
    } finally {
      setGenerating(false);
    }
  }

  async function copyPlain() {
    if (!generatedAssets) return;
    await navigator.clipboard.writeText(toCopyPlainText(generatedAssets));
    setCopyMessage('已複製：純文字格式');
  }

  async function copyTable() {
    if (!generatedAssets) return;
    await navigator.clipboard.writeText(toCopyTableText(generatedAssets));
    setCopyMessage('已複製：表格（TSV）格式');
  }

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <button
        className="btn btn-outline"
        onClick={() => navigate('/')}
        style={{ marginBottom: '1.5rem' }}
      >
        ← 返回首頁
      </button>

      <h2 style={{ marginBottom: '1.5rem' }}>✨ 自選優化（手動複製版）</h2>

      <div className="card">
        <h3 className="card-title" style={{ marginBottom: '1rem' }}>📁 選擇廣告活動</h3>
        <input
          type="text"
          list="campaign-list"
          placeholder="🔍 搜尋或選擇活動..."
          value={campaignSearch}
          onChange={(e) => {
            setCampaignSearch(e.target.value);
            const matched = campaigns.find(c => formatCampaignLabel(c) === e.target.value);
            if (matched) {
              setSelectedCampaign(matched);
              setSelectedAdGroup(null);
              setAdGroupSearch('');
            }
          }}
          style={{
            width: '100%',
            padding: '0.75rem',
            background: 'var(--color-bg-secondary)',
            border: `1px solid ${selectedCampaign ? 'var(--color-success)' : 'var(--color-border)'}`,
            borderRadius: '8px',
            color: 'var(--color-text)',
            fontSize: '1rem'
          }}
        />
        <datalist id="campaign-list">
          {filteredCampaigns.map(c => (
            <option key={c.campaign_id} value={formatCampaignLabel(c)} />
          ))}
        </datalist>
        {selectedCampaign?.window_start_date && selectedCampaign?.window_end_date && (
          <div style={{ marginTop: '0.5rem', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            資料窗口：{selectedCampaign.window_start_date} ~ {selectedCampaign.window_end_date}
          </div>
        )}
      </div>

      {selectedCampaign && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '1rem' }}>📂 選擇廣告組合</h3>
          <input
            type="text"
            list="adgroup-list"
            placeholder="🔍 搜尋或選擇群組..."
            value={adGroupSearch}
            onChange={(e) => {
              setAdGroupSearch(e.target.value);
              const matched = adGroups.find(ag => ag.ad_group_name === e.target.value);
              if (matched) setSelectedAdGroup(matched);
            }}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'var(--color-bg-secondary)',
              border: `1px solid ${selectedAdGroup ? 'var(--color-success)' : 'var(--color-border)'}`,
              borderRadius: '8px',
              color: 'var(--color-text)',
              fontSize: '1rem'
            }}
          />
          <datalist id="adgroup-list">
            {filteredAdGroups.map(ag => (
              <option key={ag.ad_group_id} value={ag.ad_group_name} />
            ))}
          </datalist>
        </div>
      )}

      {loadingBrief && <div className="card">資料分析中...</div>}

      {error && (
        <div className="card" style={{ borderColor: 'var(--color-danger)', color: 'var(--color-danger)' }}>
          {error}
        </div>
      )}

      {adGroupDetails && brief && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="card-title">🧭 優化流程（可回上一步）</h3>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>Step {step}/3</span>
          </div>

          {step === 1 && (
            <div>
              <div style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)' }}>
                分析窗：近 {brief.windowDays} 天，僅 ENABLED 廣告，最小樣本 {brief.rules.minImpressions} impressions。
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <strong>目前 ENABLED RSA 廣告數：</strong> {brief.adCountEnabled} / 3
              </div>

              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
                {brief.ads.map((ad) => (
                  <div key={ad.adId} style={{ padding: '0.75rem', background: 'var(--color-bg-secondary)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.875rem', marginBottom: '0.35rem' }}>
                      <strong>Ad ID:</strong> {ad.adId} | <strong>特化:</strong> {ad.specialization} ({ad.classificationSource})
                    </div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--color-text-secondary)' }}>
                      Impr {ad.impressions.toLocaleString()} | CTR {formatPercent(ad.ctr)} | CvR {formatPercent(ad.cvr)}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '0.75rem' }}><strong>三特化表現</strong></div>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>特化</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem' }}>廣告數</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem' }}>Impr</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem' }}>CTR</th>
                    <th style={{ textAlign: 'right', padding: '0.5rem' }}>CvR</th>
                    <th style={{ textAlign: 'left', padding: '0.5rem' }}>判斷</th>
                  </tr>
                </thead>
                <tbody>
                  {brief.specializationPerformance.map((s) => (
                    <tr key={s.specialization} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.5rem' }}>{s.specialization}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.adCount}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{s.impressions.toLocaleString()}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatPercent(s.ctr)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatPercent(s.cvr)}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.82rem' }}>{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginBottom: '1rem', color: 'var(--color-warning)' }}>
                建議優化類型：<strong>{brief.suggestedSpecialization || '無'}</strong>（{brief.suggestedReason}）
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={() => setStep(2)}>
                  下一步：選擇優化類型 →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Persona</label>
                <select
                  value={persona}
                  onChange={(e) => setPersona(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                >
                  {personaOptions.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                </select>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>使用場景（選填，可手動輸入）</label>
                <input
                  type="text"
                  list="scene-list"
                  value={scene}
                  onChange={(e) => setScene(e.target.value)}
                  placeholder="例：婚禮/婚宴、婚紗試穿、產後照護、辦公室久坐..."
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                />
                <datalist id="scene-list">
                  {adGroupDetails?.inferred_persona?.allScenes?.map((s: string) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
                <div style={{ marginTop: '0.4rem', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                  留空則由 AI 自動判斷。
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Meta 貼文（選填）</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.5rem' }}>
                  <input
                    type="url"
                    value={metaPostUrl}
                    onChange={(e) => setMetaPostUrl(e.target.value)}
                    placeholder="貼文連結（目前支援 Facebook）"
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)' }}
                  />
                  <textarea
                    value={metaPostText}
                    onChange={(e) => setMetaPostText(e.target.value)}
                    placeholder="貼文內容（建議貼上，作為文案基底）"
                    rows={3}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid var(--color-border)', resize: 'vertical' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>待優化特化型（可手動改）</label>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {SPECIALIZATION_OPTIONS.map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="radio"
                        name="specialization"
                        checked={selectedSpecialization === opt}
                        onChange={() => setSelectedSpecialization(opt)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  品牌資料庫名人素材（未選時會使用全部可用名人）
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {(referenceLibrary?.celebrities.active || []).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="btn"
                      onClick={() => toggleNamedValue(item.name, selectedCelebrityNames, setSelectedCelebrityNames)}
                      style={{
                        background: selectedCelebrityNames.includes(item.name) ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                        color: selectedCelebrityNames.includes(item.name) ? 'white' : 'var(--color-text)',
                        border: '1px solid var(--color-border)'
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  品牌資料庫活動素材（未選時會使用全部可用活動）
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {(referenceLibrary?.activities.active || []).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="btn"
                      onClick={() => toggleNamedValue(item.name, selectedActivityNames, setSelectedActivityNames)}
                      style={{
                        background: selectedActivityNames.includes(item.name) ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                        color: selectedActivityNames.includes(item.name) ? 'white' : 'var(--color-text)',
                        border: '1px solid var(--color-border)'
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                {referenceLibrary && (referenceLibrary.summary.expiredCelebrities > 0 || referenceLibrary.summary.expiredActivities > 0) && (
                  <div style={{ marginTop: '0.5rem', color: 'var(--color-warning)', fontSize: '0.85rem' }}>
                    品牌資料庫有過期素材，系統不會使用，請至品牌資料庫清理。
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>Keywords（可手動複核）</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {keywords.map((kw, i) => (
                    <span key={`${kw.keyword_text}-${i}`} style={{
                      background: 'var(--color-primary)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '999px',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {kw.keyword_text}
                      <span style={{
                        fontSize: '0.75rem',
                        opacity: 0.8,
                        marginLeft: '0.25rem'
                      }}>
                        [{kw.match_type === 'EXACT' ? '完全' : kw.match_type === 'PHRASE' ? '詞組' : '廣泛'}]
                      </span>
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button className="btn btn-outline" onClick={() => setStep(1)}>← 上一步</button>
                <button className="btn btn-primary" onClick={generateAssets} disabled={generating}>
                  {generating ? '生成中...' : '生成 15+4 →'}
                </button>
              </div>
            </div>
          )}

          {step === 3 && generatedAssets && (
            <div>
              <div style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)' }}>
                已依「{selectedSpecialization}」生成 1 組完整 RSA（15 Headlines + 4 Descriptions）。
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                <button className="btn btn-outline" onClick={copyPlain}>複製純文字</button>
                <button className="btn btn-outline" onClick={copyTable}>複製表格（TSV）</button>
                {copyMessage && <span style={{ color: 'var(--color-success)', fontSize: '0.875rem' }}>{copyMessage}</span>}
              </div>

              <div style={{ marginBottom: '0.5rem' }}><strong>Headlines（15）</strong></div>
              <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
                {generatedAssets.headlines.map((h, i) => (
                  <div
                    key={h.id}
                    style={{ padding: '0.5rem', background: 'var(--color-bg-secondary)', borderRadius: '6px', cursor: 'pointer' }}
                    onClick={() => navigator.clipboard.writeText(h.text)}
                    title="點擊複製"
                  >
                    {i + 1}. [{h.type}] {h.text}
                  </div>
                ))}
              </div>

              <div style={{ marginBottom: '0.5rem' }}><strong>Descriptions（4）</strong></div>
              <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
                {generatedAssets.descriptions.map((d, i) => (
                  <div
                    key={d.id}
                    style={{ padding: '0.5rem', background: 'var(--color-bg-secondary)', borderRadius: '6px', cursor: 'pointer' }}
                    onClick={() => navigator.clipboard.writeText(d.text)}
                    title="點擊複製"
                  >
                    {i + 1}. {d.text}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <button className="btn btn-outline" onClick={() => setStep(2)}>← 上一步</button>
                <button className="btn btn-primary" onClick={generateAssets} disabled={generating}>
                  {generating ? '生成中...' : '重新生成'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
